import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, FileSignature, Loader2, Plus, Trash2, Users, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import MitgliedSuche from '@/components/MitgliedSuche';
import SendenDialog from '@/components/unterschrift/SendenDialog';
import { sammelvertragAPI, unterschriftAPI } from '@/lib/api';
import { TARIFE, LAUFZEIT_LABEL, laufzeitenFuer, planId, fmtEuro } from '@/lib/tarife';

/* Geschwister in einem Vertrag (Betreiber 26.10.2026: „alle in einem Vertrag abschließen“). Ein Elternteil, 2–6 Kinder:
   EIN Dokument, EINE Unterschrift der Eltern, EIN SEPA-Mandat; je Kind eine eigene Mitgliedschaft. Geschwisterrabatt
   rechnet der Server automatisch (das teuerste Kind zahlt voll). Beiträge zeigt die Vorschau des Servers, nicht der
   Browser. Abgeschlossen wird an der Unterschrift-Station (Theken-iPad oder QR), Art `sammelvertrag`.
   Geschwister dazubuchen (26.10.2026): Kinder mit laufendem Vertrag stehen als „Bereits Mitglied“ — ihr Vertrag bleibt
   unverändert; dann reicht ein neues Kind, es bekommt den Geschwisterrabatt und bei gleicher IBAN dasselbe Mandat. */
const KINDER_TARIFE = TARIFE.filter(t => t.linie === 'kinder');
const heute = () => new Date().toISOString().slice(0, 10);
const leerKind = () => ({ an: true, lead_id: null, vorname: '', nachname: '', geburtsdatum: '', tarif: 'performance_kids', laufzeit: 'm12', laufend: null,
  indiv: false, indivBeitrag: '', indivGrund: '' });
const zahl = (t) => Number(String(t || '').replace(',', '.'));

function Feld({ label, children }) {
  return <label className="space-y-1 text-xs min-w-0"><span className="font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>{children}</label>;
}

export default function SammelvertragPage() {
  const [params] = useSearchParams();
  const [suche, setSuche] = useState(null);
  const [eltern, setEltern] = useState({ name: '', telefon: '', email: '', strasse: '', plz: '', ort: '' });
  const [kinder, setKinder] = useState([leerKind(), leerKind()]);
  const [bestehend, setBestehend] = useState([]);       // Geschwister mit laufendem Vertrag (bleibt unverändert)
  const [sepa, setSepa] = useState({ kontoinhaber: '', iban: '' });
  const [beginn, setBeginn] = useState(heute());
  const [tag1, setTag1] = useState(false);
  const [vorschau, setVorschau] = useState(null);
  const [fehler, setFehler] = useState('');
  const [laedt, setLaedt] = useState(false);
  const [vorgang, setVorgang] = useState(null);
  const [sendet, setSendet] = useState(false);
  const [ergebnis, setErgebnis] = useState(null);

  const laden = async (leadId) => {
    if (!leadId) return;
    try {
      const f = (await sammelvertragAPI.familie(leadId)).data;
      setEltern({ name: f.eltern.name || '', telefon: f.eltern.telefon || '', email: f.eltern.email || '', strasse: f.eltern.strasse || '', plz: f.eltern.plz || '', ort: f.eltern.ort || '' });
      setSepa(s => ({ ...s, kontoinhaber: s.kontoinhaber || f.eltern.name || '' }));
      const ks = f.kinder.filter(k => !k.laufender_vertrag).map(k => ({ ...leerKind(), lead_id: k.lead_id, vorname: k.vorname, nachname: k.nachname, geburtsdatum: k.geburtsdatum }));
      const be = f.kinder.filter(k => k.laufender_vertrag);
      setBestehend(be);
      setKinder(ks.length ? ks : (be.length ? [leerKind()] : [leerKind(), leerKind()]));
    } catch { toast.error('Familie konnte nicht geladen werden'); }
  };
  useEffect(() => { const l = params.get('lead'); if (l) laden(l); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const kindSetzen = (i, k, v) => setKinder(ks => ks.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const gewaehlt = kinder.filter(k => k.an && k.vorname.trim());
  const entwurf = useMemo(() => ({
    erziehungsberechtigter_name: eltern.name.trim(), erziehungsberechtigter_telefon: eltern.telefon.trim(),
    erziehungsberechtigter_email: eltern.email.trim().toLowerCase(), strasse: eltern.strasse, plz: eltern.plz, ort: eltern.ort,
    kontoinhaber: sepa.kontoinhaber.trim(), iban: sepa.iban.replace(/\s/g, ''), vertragsbeginn: beginn, tag1_angebot: tag1,
    kinder: gewaehlt.map(k => ({ vorname: k.vorname.trim(), nachname: k.nachname.trim(), geburtsdatum: k.geburtsdatum || '',
      plan_id: planId(k.tarif, k.laufzeit), lead_id: k.lead_id,
      // Individuelle Vereinbarung (26.10.2026): freier Monatsbeitrag mit Grund, ohne automatischen Rabatt
      ...(k.indiv && zahl(k.indivBeitrag) > 0 ? { individueller_beitrag: zahl(k.indivBeitrag), individuell_grund: k.indivGrund.trim() } : {}) })),
    bestehende: bestehend.map(b => b.lead_id),
  }), [eltern, sepa, beginn, tag1, gewaehlt, bestehend]); // eslint-disable-line react-hooks/exhaustive-deps
  const schluessel = JSON.stringify(entwurf);
  const vollstaendig = entwurf.kinder.length >= 1 && entwurf.kinder.length + entwurf.bestehende.length >= 2 && entwurf.erziehungsberechtigter_name.length >= 3
    && (entwurf.erziehungsberechtigter_telefon || entwurf.erziehungsberechtigter_email) && entwurf.kontoinhaber && entwurf.iban.length >= 15;

  // Vorschau vom Server (dieselbe Rechnung wie der Abschluss), leicht verzögert
  useEffect(() => {
    if (!vollstaendig) { setVorschau(null); setFehler(''); return undefined; }
    let aktiv = true;
    const t = setTimeout(async () => {
      setLaedt(true);
      try { const r = await sammelvertragAPI.vorschau(entwurf); if (aktiv) { setVorschau(r.data); setFehler(''); } }
      catch (e) { if (aktiv) { setVorschau(null); const d = e?.response?.data?.detail; setFehler(typeof d === 'string' ? d : 'Vorschau nicht möglich — Angaben prüfen'); } }
      if (aktiv) setLaedt(false);
    }, 450);
    return () => { aktiv = false; clearTimeout(t); };
  }, [schluessel, vollstaendig]); // eslint-disable-line react-hooks/exhaustive-deps

  const senden = async () => {
    if (!vorschau) { toast.error('Erst müssen Vorschau und Angaben stimmen'); return; }
    setSendet(true);
    try { setVorgang((await unterschriftAPI.senden(entwurf, 'sammelvertrag')).data); }
    catch (e) { const d = e?.response?.data?.detail; toast.error(typeof d === 'string' ? d : 'Konnte nicht zur Unterschrift gesendet werden'); }
    setSendet(false);
  };
  const pdfOeffnen = async (id) => {
    try { const r = await sammelvertragAPI.pdf(id); window.open(URL.createObjectURL(r.data), '_blank', 'noopener'); }
    catch { toast.error('PDF konnte nicht geladen werden'); }
  };

  if (ergebnis) {
    return (
      <div className="max-w-2xl space-y-4" data-testid="sammel-ergebnis">
        <h1 className="text-xl font-bold flex items-center gap-2"><Users className="w-5 h-5 text-marke" /> Geschwistervertrag unterschrieben</h1>
        <Card><CardContent className="p-5 space-y-3 text-sm">
          <p>{ergebnis.message}</p>
          <ul className="space-y-1">{(ergebnis.vertraege || []).map(v => <li key={v.id}>✓ {v.full_name}: {v.plan_name} {v.duration_label}, {fmtEuro(v.monthly_price)} monatlich · {v.vertragsnummer}</li>)}</ul>
          {ergebnis.pdf_versand && <p className="text-xs text-muted-foreground">{ergebnis.pdf_versand.status === 'gesendet' ? `PDF an ${ergebnis.pdf_versand.an} gesendet.` : `PDF nicht per E-Mail gesendet (${ergebnis.pdf_versand.grund || ergebnis.pdf_versand.status}) — bitte herunterladen und mitgeben.`}</p>}
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => pdfOeffnen(ergebnis.sammelvertrag_id)}><FileText className="w-4 h-4" /> PDF öffnen</Button>
            {ergebnis.lead_id && <Link to={`/leads/${ergebnis.lead_id}`}><Button variant="outline" size="sm">Zum ersten Kind</Button></Link>}
            <Button size="sm" onClick={() => { setErgebnis(null); setVorschau(null); }}>Fertig</Button>
          </div>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-4" data-testid="sammelvertrag">
      <div className="flex items-center gap-2">
        <Link to="/verkauf" className="text-muted-foreground hover:text-foreground"><ArrowLeft className="w-4 h-4" /></Link>
        <h1 className="text-xl font-bold flex items-center gap-2"><Users className="w-5 h-5 text-marke" /> Geschwister in einem Vertrag</h1>
      </div>
      <p className="text-sm text-muted-foreground">Ein Dokument, eine Unterschrift der Eltern, ein SEPA-Mandat. Jedes Kind bekommt seine eigene Mitgliedschaft. Den Geschwisterrabatt rechnet das CRM automatisch: Das teuerste Kind zahlt voll, alle weiteren bekommen den Rabatt, wenn ihr Tarif ihn zulässt (ab 12 Monaten).</p>

      <Card><CardContent className="p-4 space-y-3 text-sm">
        <p className="font-semibold">Familie</p>
        <MitgliedSuche wert={suche} onWahl={(w) => { setSuche(w); if (w) laden(w.id); }} placeholder="Elternteil oder Kind suchen (lädt die Familie)…" tid="sammel-suche" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Feld label="Erziehungsberechtigte/r *"><Input value={eltern.name} onChange={e => setEltern(p => ({ ...p, name: e.target.value }))} className="h-9" data-testid="sammel-eltern-name" /></Feld>
          <Feld label="Handynummer"><Input value={eltern.telefon} onChange={e => setEltern(p => ({ ...p, telefon: e.target.value }))} className="h-9" placeholder="+49 …" /></Feld>
          <Feld label="E-Mail (PDF geht hierhin)"><Input value={eltern.email} onChange={e => setEltern(p => ({ ...p, email: e.target.value }))} className="h-9" /></Feld>
          <Feld label="Straße"><Input value={eltern.strasse} onChange={e => setEltern(p => ({ ...p, strasse: e.target.value }))} className="h-9" /></Feld>
          <Feld label="PLZ"><Input value={eltern.plz} onChange={e => setEltern(p => ({ ...p, plz: e.target.value }))} className="h-9" /></Feld>
          <Feld label="Ort"><Input value={eltern.ort} onChange={e => setEltern(p => ({ ...p, ort: e.target.value }))} className="h-9" /></Feld>
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-4 space-y-3 text-sm" data-testid="sammel-kinder">
        {bestehend.length > 0 && (
          <div className="space-y-1" data-testid="sammel-bestehend">
            <p className="font-semibold">Bereits Mitglied — Vertrag bleibt unverändert</p>
            {bestehend.map(b => (
              <div key={b.lead_id} className="flex items-center gap-2 rounded-lg bg-muted/40 px-3 py-2 text-xs">
                <span className="font-medium">{[b.vorname, b.nachname].filter(Boolean).join(' ')}</span>
                <span className="text-muted-foreground">{b.laufender_vertrag}</span>
                <Button type="button" variant="ghost" size="sm" className="ml-auto h-7 text-xs" onClick={() => setBestehend(bs => bs.filter(x => x.lead_id !== b.lead_id))} title="Nicht als Geschwister zählen">entfernen</Button>
              </div>
            ))}
          </div>
        )}
        <p className="font-semibold">{bestehend.length ? 'Neu dazu' : 'Kinder'}</p>
        {kinder.map((k, i) => (
          <div key={i} className={`rounded-lg border p-3 space-y-2 ${k.an ? 'border-border/60' : 'border-dashed opacity-60'}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="flex items-center gap-1.5 text-xs font-medium"><input type="checkbox" className="accent-marke" checked={k.an} onChange={e => kindSetzen(i, 'an', e.target.checked)} /> im Vertrag</label>
              <Button type="button" variant="ghost" size="icon" className="h-7 w-7 ml-auto" onClick={() => setKinder(ks => ks.filter((_, j) => j !== i))} title="Kind entfernen"><Trash2 className="w-4 h-4" /></Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <Feld label="Vorname"><Input value={k.vorname} onChange={e => kindSetzen(i, 'vorname', e.target.value)} className="h-9" data-testid={`sammel-kind-vorname-${i}`} /></Feld>
              <Feld label="Nachname"><Input value={k.nachname} onChange={e => kindSetzen(i, 'nachname', e.target.value)} className="h-9" placeholder="wie Eltern" /></Feld>
              <Feld label="Geburtsdatum"><Input type="date" value={k.geburtsdatum} onChange={e => kindSetzen(i, 'geburtsdatum', e.target.value)} className="h-9" /></Feld>
              <Feld label="Tarif">
                <select value={k.tarif} onChange={e => { const t = KINDER_TARIFE.find(x => x.id === e.target.value); setKinder(ks => ks.map((x, j) => (j === i ? { ...x, tarif: t.id, laufzeit: laufzeitenFuer(t).includes(x.laufzeit) ? x.laufzeit : laufzeitenFuer(t)[0] } : x))); }}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
                  {KINDER_TARIFE.map(t => <option key={t.id} value={t.id}>{t.name.replace('P1 ', '')}</option>)}
                </select>
              </Feld>
              <Feld label="Laufzeit">
                <select value={k.laufzeit} onChange={e => kindSetzen(i, 'laufzeit', e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
                  {laufzeitenFuer(KINDER_TARIFE.find(t => t.id === k.tarif)).map(lz => <option key={lz} value={lz}>{LAUFZEIT_LABEL[lz]}</option>)}
                </select>
              </Feld>
            </div>
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <label className="flex items-center gap-1.5"><input type="checkbox" className="accent-marke" checked={k.indiv} onChange={e => kindSetzen(i, 'indiv', e.target.checked)} data-testid={`sammel-kind-indiv-${i}`} /> Individuelle Vereinbarung</label>
              {k.indiv && <>
                <Input inputMode="decimal" value={k.indivBeitrag} onChange={e => kindSetzen(i, 'indivBeitrag', e.target.value)} placeholder="Monatsbeitrag €" className="h-8 w-32" />
                <Input value={k.indivGrund} onChange={e => kindSetzen(i, 'indivGrund', e.target.value)} placeholder="Grund (Pflicht)" className="h-8 flex-1 min-w-[10rem]" />
              </>}
            </div>
          </div>
        ))}
        {kinder.length < 6 && <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => setKinder(ks => [...ks, leerKind()])}><Plus className="w-3.5 h-3.5" /> weiteres Kind</Button>}
      </CardContent></Card>

      <Card><CardContent className="p-4 space-y-3 text-sm">
        <p className="font-semibold">Beginn und SEPA</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Feld label="Vertragsbeginn (alle Kinder)"><Input type="date" value={beginn} onChange={e => setBeginn(e.target.value)} className="h-9" /></Feld>
          <Feld label="Kontoinhaber/in *"><Input value={sepa.kontoinhaber} onChange={e => setSepa(p => ({ ...p, kontoinhaber: e.target.value }))} className="h-9" /></Feld>
          <Feld label="IBAN *"><Input value={sepa.iban} onChange={e => setSepa(p => ({ ...p, iban: e.target.value }))} className="h-9" placeholder="DE…" data-testid="sammel-iban" /></Feld>
        </div>
        <label className="flex items-center gap-2 text-xs"><input type="checkbox" className="accent-marke" checked={tag1} onChange={e => setTag1(e.target.checked)} /> Am Beratungstag abgeschlossen (Betreuter Einstieg zum Tag-1-Preis)</label>
      </CardContent></Card>

      <Card><CardContent className="p-4 space-y-2 text-sm" data-testid="sammel-vorschau">
        <p className="font-semibold flex items-center gap-2">Was die Familie zahlt {laedt && <Loader2 className="w-4 h-4 animate-spin" />}</p>
        {!vollstaendig && <p className="text-muted-foreground text-xs">Mindestens zwei Kinder (neu oder schon Mitglied), davon eines neu mit Vornamen, Erziehungsberechtigte/r mit Telefon oder E-Mail, Kontoinhaber und IBAN eintragen.</p>}
        {fehler && <p className="text-xs rounded-md bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300 px-3 py-2">{fehler}</p>}
        {vorschau && (
          <>
            <ul className="divide-y divide-border/50">
              {(vorschau.bestehende || []).map(b => (
                <li key={b.id} className="py-1.5 flex flex-wrap gap-x-3 items-baseline text-muted-foreground">
                  <span className="font-medium">{b.full_name}</span><span className="text-xs">{b.plan_name} · läuft unverändert</span>
                  <span className="ml-auto tabular-nums">{fmtEuro(b.monthly_price)} / Monat</span>
                </li>
              ))}
              {vorschau.kinder.map((v, i) => (
                <li key={i} className="py-1.5 flex flex-wrap gap-x-3 gap-y-0.5 items-baseline">
                  <span className="font-medium">{v.full_name}</span>
                  <span className="text-muted-foreground text-xs">{v.plan_name} · {v.duration_label}</span>
                  <span className="ml-auto tabular-nums">{fmtEuro(v.monthly_price)} / Monat</span>
                  {v.geschwister_rabatt && <span className="w-full text-[11px] text-emerald-700 dark:text-emerald-400">Geschwisterrabatt −{fmtEuro(v.geschwister_abzug_woche)} je Woche</span>}
                  {v.individueller_beitrag && <span className="w-full text-[11px] text-muted-foreground">Individuell vereinbart (Listenpreis {fmtEuro(v.individueller_beitrag.listenpreis_monatlich)} monatlich)</span>}
                  {v.betreuter_einstieg_betrag > 0 && <span className="w-full text-[11px] text-muted-foreground">Betreuter Einstieg einmalig {fmtEuro(v.betreuter_einstieg_betrag)}</span>}
                </li>
              ))}
            </ul>
            <p className="font-semibold tabular-nums">{vorschau.bestehende?.length ? `Neu ${fmtEuro(vorschau.summe_monat)} monatlich, Familie zusammen ${fmtEuro(vorschau.summe_familie_monat)}` : `Zusammen ${fmtEuro(vorschau.summe_monat)} monatlich`}{vorschau.summe_einmalig ? `, einmalig ${fmtEuro(vorschau.summe_einmalig)}` : ''}</p>
            {vorschau.mandat_referenz && <p className="text-xs text-muted-foreground">Gleiche IBAN wie bisher: der neue Vertrag läuft über das bestehende SEPA-Mandat {vorschau.mandat_referenz}.</p>}
          </>
        )}
        <div className="flex justify-end pt-2">
          <Button onClick={senden} disabled={!vorschau || sendet} className="gap-1.5 bg-marke text-white hover:bg-marke-dunkel" data-testid="sammel-senden">
            {sendet ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />} Zur Unterschrift senden (iPad / QR)
          </Button>
        </div>
      </CardContent></Card>

      {vorgang && (
        <SendenDialog vorgang={vorgang} sendet={sendet} onSchliessen={() => setVorgang(null)} onErneut={senden}
          onFertig={(r) => { setVorgang(null); setErgebnis(r); toast.success(r.message || 'Geschwistervertrag unterschrieben'); }} />
      )}
    </div>
  );
}
